import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchPacientes, fetchClinicalRecords, fetchVisualRecords } from '../../servicios/apiClinica';
import CertificadoMedico from './CertificadoMedico';
import RegistroFotografico from './RegistroFotografico';
import VisorConsultaModal from './VisorConsultaModal';
import HistoriaClinicaVisual from './HistoriaClinicaVisual';
import HistorialFormulasModal from './HistorialFormulasModal';
import FichaPacienteModal from './FichaPacienteModal';
import ModalRecordatorio from './ModalRecordatorio';
import ModalFactura from './ModalFactura';
import CumpleanosProximos from './CumpleanosProximos';
import ConfirmModal from '../../comun/ConfirmModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const Pacientes = () => {
    const navigate = useNavigate();
    const [pacientes, setPacientes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCertificadoModal, setShowCertificadoModal] = useState(false);
    const [showRegistroFotograficoModal, setShowRegistroFotograficoModal] = useState(false);
    const [showConsultaModal, setShowConsultaModal] = useState(false);
    const [showVisualModal, setShowVisualModal] = useState(false);
    const [showFormulasModal, setShowFormulasModal] = useState(false);
    const [selectedPatientForVisual, setSelectedPatientForVisual] = useState(null);
    const [selectedVisualRecord, setSelectedVisualRecord] = useState(null);
    const [previousVisualRecord, setPreviousVisualRecord] = useState(null);
    const [visualViewOnly, setVisualViewOnly] = useState(false);
    const [visualKey, setVisualKey] = useState(0);
    const [fichaPatient, setFichaPatient] = useState(null);
    const [visualRecordsList, setVisualRecordsList] = useState([]);
    const [selectedQuote, setSelectedQuote] = useState(null);
    const [selectedPatient, setSelectedPatient] = useState(null);
    const [clinicalData, setClinicalData] = useState(null);
    const [showObsModal, setShowObsModal] = useState(false);
    const [selectedObsPatient, setSelectedObsPatient] = useState(null);
    const [observations, setObservations] = useState([]);
    const [savingObs, setSavingObs] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [showMissingFieldsModal, setShowMissingFieldsModal] = useState(false);
    const [missingFields, setMissingFields] = useState([]);
    
    // Filtros y ergonomía
    const [filtroVista, setFiltroVista] = useState('clinico'); // 'clinico', 'agenda'
    const [filtroControl, setFiltroControl] = useState('todos'); // 'todos', 'al_dia', 'proximo', 'vencido'
    const [searchTerm, setSearchTerm] = useState('');
    const [activeDropdownId, setActiveDropdownId] = useState(null);

    const loadPacientes = async (autoCompleteQuoteId = null, autoCompletePatientId = null) => {
        setLoading(true);
        const data = await fetchPacientes();
        
        try {
            const token = localStorage.getItem('token');
            const recordsResponse = await fetch(`${API_URL}/api/clinical-records/?limit=1000`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            let allRecords = [];
            if (recordsResponse.ok) {
                const rData = await recordsResponse.json();
                allRecords = rData.results || rData || [];
            }
            
            const pacientesConHistorial = (data || []).map(p => {
                const pRecords = allRecords.filter(r => String(r.patient) === String(p.id) || (r.patient && String(r.patient.id) === String(p.id)));
                const hasRealHistory = pRecords.some(r => r.reason_for_consultation !== 'Cotización / Presupuesto' && r.reason_for_consultation !== 'Cotización / Presupuesto (Completado)');
                const esPresupuesto = (r) => r.reason_for_consultation === 'Cotización / Presupuesto' || r.reason_for_consultation === 'Cotización / Presupuesto (Completado)';
                const quoteRecord = pRecords.find(r => esPresupuesto(r) && !r.anulada) || pRecords.find(esPresupuesto);
                return { ...p, hasRealHistory, quoteRecord };
            });
            pacientesConHistorial.sort((a, b) => {
                const dateA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
                const dateB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
                return dateB - dateA;
            });
            setPacientes(pacientesConHistorial);

            if (autoCompleteQuoteId || autoCompletePatientId) {
                const targetPatient = pacientesConHistorial.find(p => 
                    (autoCompletePatientId && String(p.id) === String(autoCompletePatientId)) ||
                    (autoCompleteQuoteId && p.quoteRecord && String(p.quoteRecord.id) === String(autoCompleteQuoteId))
                );
                if (targetPatient) {
                    handleCompletarHistorial(targetPatient);
                }
            }
        } catch (e) {
            console.error(e);
            setPacientes(data || []);
        }
        
        setLoading(false);
    };

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const qId = urlParams.get('quote_id');
        const pId = urlParams.get('patient_id');
        loadPacientes(qId, pId);
    }, []);

    // Listener para cerrar dropdowns al hacer clic fuera
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (!e.target.closest('.patient-dropdown-container')) {
                setActiveDropdownId(null);
            }
        };
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, []);

    const handleSave = () => {
        loadPacientes();
    };

    const handleOpenObservations = (p) => {
        setSelectedObsPatient(p);
        let obsList = p.clinical_observations;
        if (typeof obsList === 'string') {
            try { obsList = JSON.parse(obsList); } catch(e) { obsList = []; }
        }
        setObservations(Array.isArray(obsList) ? [...obsList] : []);
        setShowObsModal(true);
        setActiveDropdownId(null);
    };

    const handleAddObservation = () => {
        setObservations([...observations, { id: Date.now(), text: '', resolved: false }]);
    };

    const handleUpdateObservation = (index, field, value) => {
        const newObs = [...observations];
        newObs[index][field] = value;
        setObservations(newObs);
    };

    const handleSaveObservations = async () => {
        if (!selectedObsPatient) return;
        setSavingObs(true);
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/patients/${selectedObsPatient.id}/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    clinical_observations: observations
                })
            });
            if (response.ok) {
                setShowObsModal(false);
                loadPacientes();
            } else {
                alert('Error al guardar observaciones');
            }
        } catch (e) {
            console.error(e);
            alert('Error de conexión');
        }
        setSavingObs(false);
    };

    const handleCreate = () => {
        handleOpenVisualRecord(null);
    };

    const handlePromoteToClinical = async (paciente) => {
        if (!window.confirm(`¿Estás seguro de que deseas mover a ${paciente.first_name || 'Paciente'} al historial clínico?`)) return;
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/api/patients/${paciente.id}/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ is_clinical: true })
            });
            if (response.ok) {
                alert('Paciente movido exitosamente al historial clínico.');
                loadPacientes();
            } else {
                alert('Error al actualizar el estado del paciente.');
            }
        } catch (e) {
            console.error(e);
            alert('Error de conexión.');
        }
    };

    const loadPatientClinicalData = async (patientId) => {
        const records = await fetchClinicalRecords(patientId);
        if (records && records.length > 0) {
            const isQuote = (r) => r.reason_for_consultation === 'Cotización / Presupuesto';
            const isCompletedQuote = (r) => r.reason_for_consultation === 'Cotización / Presupuesto (Completado)';

            const realHistories = records.filter(r => !isQuote(r) && !isCompletedQuote(r));
            const basePool = realHistories.length > 0
                ? realHistories
                : records.filter(isCompletedQuote);

            if (basePool.length === 0 && records.length > 0) {
                setClinicalData(records[0] || null);
                return;
            }

            const score = (r) => {
                const rows = Array.isArray(r.treatments) ? r.treatments : [];
                const conTexto = rows.filter(t =>
                    (t.procedures && String(t.procedures).trim()) ||
                    (t.diagnostics_complications && String(t.diagnostics_complications).trim()) ||
                    (t.prescriptions && String(t.prescriptions).trim())
                ).length;
                return { texto: conTexto, total: rows.length };
            };

            const baseRecord = basePool.reduce((best, r) => {
                const s = score(r);
                const b = score(best);
                if (s.texto > b.texto) return r;
                if (s.texto === b.texto && s.total > b.total) return r;
                if (s.texto === b.texto && s.total === b.total && new Date(r.updated_at || 0) > new Date(best.updated_at || 0)) return r;
                return best;
            }, basePool[0]);

            const hasSession = (t) => t && (!!t.date || !!t.appointment_id);
            const textLength = (t) => `${t.procedures || ''}${t.diagnostics_complications || ''}${t.prescriptions || ''}`.trim().length;

            const seen = new Map();
            records.forEach(r => {
                (r.treatments || []).forEach(t => {
                    if (!hasSession(t)) return;
                    const key = t.appointment_id || `${t.date || ''}|${t.time || ''}|${t.procedures || ''}|${t.diagnostics_complications || ''}`;
                    if (!seen.has(key)) {
                        seen.set(key, t);
                    } else if (t.appointment_id && textLength(t) > textLength(seen.get(key))) {
                        seen.set(key, t);
                    }
                });
            });

            setClinicalData({ ...baseRecord, treatments: Array.from(seen.values()) });
        } else {
            setClinicalData(null);
        }
    };

    // modo 'ver'    → abre la historia (o la última del paciente) solo para consultar.
    // modo 'editar' → abre la historia (o la última) para corregirla.
    // modo 'nuevo'  → examen NUEVO; la última historia solo precarga "RX en uso" y "último control".
    // Si el paciente no tiene historias, 'ver' y 'editar' también abren un examen nuevo.
    const handleOpenVisualRecord = async (patient = null, record = null, modo = 'ver') => {
        setSelectedPatientForVisual(patient);
        setActiveDropdownId(null);
        setPreviousVisualRecord(null);
        setVisualKey(k => k + 1);

        let historia = record;
        let ultima = null;
        if (!historia && patient?.id) {
            try {
                const recs = await fetchVisualRecords(patient.id);
                ultima = [...(recs || [])].filter(r => !r.anulada).sort((a, b) => new Date(b.date || b.created_at) - new Date(a.date || a.created_at))[0] || null;
            } catch (e) {
                console.error('Error fetching visual record for patient:', e);
            }
            if (modo !== 'nuevo') historia = ultima;
        }

        if (historia) {
            setSelectedVisualRecord(historia);
            setVisualViewOnly(modo === 'ver');
        } else {
            setSelectedVisualRecord(null);
            setPreviousVisualRecord(ultima);
            setVisualViewOnly(false);
        }
        setShowVisualModal(true);
    };

    const handleOpenFormulasEvolution = async (patient) => {
        setSelectedPatientForVisual(patient);
        setActiveDropdownId(null);
        try {
            const recs = await fetchVisualRecords(patient.id);
            setVisualRecordsList(recs || []);
            setShowFormulasModal(true);
        } catch (e) {
            console.error('Error fetching visual records for evolution:', e);
            setVisualRecordsList([]);
            setShowFormulasModal(true);
        }
    };

    const getCheckupStatus = (patient) => {
        if (!patient?.next_checkup_date) {
            return {
                status: 'none',
                label: 'Sin control agendado',
                badgeStyle: { backgroundColor: '#f3f4f6', color: '#6b7280', border: '1px solid #e5e7eb' },
                daysDiff: null
            };
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const nextDate = new Date(patient.next_checkup_date);
        nextDate.setHours(0, 0, 0, 0);

        const diffTime = nextDate.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            return {
                status: 'expired',
                label: `Vencido (${patient.next_checkup_date})`,
                badgeStyle: { backgroundColor: '#fee2e2', color: '#991b1b', border: '1px solid #f87171' },
                daysDiff: diffDays,
                nextDateStr: patient.next_checkup_date
            };
        } else if (diffDays <= 30) {
            return {
                status: 'upcoming',
                label: `Próximo: ${patient.next_checkup_date} (${diffDays}d)`,
                badgeStyle: { backgroundColor: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' },
                daysDiff: diffDays,
                nextDateStr: patient.next_checkup_date
            };
        } else {
            return {
                status: 'ok',
                label: `Al día (${patient.next_checkup_date})`,
                badgeStyle: { backgroundColor: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' },
                daysDiff: diffDays,
                nextDateStr: patient.next_checkup_date
            };
        }
    };

    // Recordatorio por correo (Resend): los automáticos salen solos; este abre la ventana de envío manual.
    const [recordatorioPatient, setRecordatorioPatient] = useState(null);
    const [facturaPatient, setFacturaPatient] = useState(null);

    const getWhatsAppRecallLink = (patient, checkupInfo) => {
        const rawPhone = (patient?.phone || '').replace(/[^0-9]/g, '');
        if (!rawPhone) return null;

        let cleanPhone = rawPhone;
        if (cleanPhone.startsWith('09') && cleanPhone.length === 10) {
            cleanPhone = '593' + cleanPhone.substring(1);
        } else if (cleanPhone.length === 9 && cleanPhone.startsWith('9')) {
            cleanPhone = '593' + cleanPhone;
        }

        const patientName = `${patient?.first_name || ''} ${patient?.last_name || ''}`.trim() || 'Estimado/a paciente';
        let msg = '';
        if (checkupInfo.status === 'expired') {
            msg = `Hola ${patientName}, le saludamos de Óptica Vision Click. Le recordamos que su control visual periódico programado para el ${checkupInfo.nextDateStr} ya se encuentra vencido. Le invitamos a agendar su cita para evaluar su salud visual y graduación. ¡Escríbanos para reservar su turno!`;
        } else {
            msg = `Hola ${patientName}, le saludamos de Óptica Vision Click. Le recordamos que su próximo control visual está programado para el ${checkupInfo.nextDateStr}. Puede responder este mensaje para agendar su cita y revisar su salud visual.`;
        }

        return `https://wa.me/${cleanPhone}?text=${encodeURIComponent(msg)}`;
    };

    const handleView = (patient) => {
        setActiveDropdownId(null);
        setFichaPatient(patient);
    };

    const handleCompletarHistorial = (patient) => {
        handleOpenVisualRecord(patient, null, 'nuevo');
    };

    const handleViewCertificado = async (patient) => {
        setActiveDropdownId(null);
        const missing = [];
        if (!patient.first_name) missing.push("Nombre(s)");
        if (!patient.last_name) missing.push("Apellido(s)");
        if (!patient.cedula) missing.push("Cédula");
        if (!patient.birth_date) missing.push("Fecha de Nacimiento");
        if (!patient.address) missing.push("Dirección (Domicilio)");

        if (missing.length > 0) {
            setMissingFields(missing);
            setShowMissingFieldsModal(true);
            return;
        }

        setSelectedPatient(patient);
        await loadPatientClinicalData(patient.id);
        setShowCertificadoModal(true);
    };

    const handleRegistroFotografico = (patient) => {
        setSelectedPatient(patient);
        setActiveDropdownId(null);
        setShowRegistroFotograficoModal(true);
    };

    const handleViewConsulta = async (patient) => {
        setSelectedPatient(patient);
        setActiveDropdownId(null);
        if (patient.quoteRecord) {
            const r = patient.quoteRecord;
            const quoteDate = new Date(r.updated_at || r.created_at);
            const savedDiscount = r.diagnostic_plans?.discountPercentage !== undefined 
                ? r.diagnostic_plans.discountPercentage 
                : (r.discountPercentage || 0);

            setSelectedQuote({
                id: r.id,
                db_patient_id: patient.id,
                status: r.reason_for_consultation?.includes('Completado') ? 'Completada' : 'Borrador',
                date: quoteDate.toLocaleDateString(),
                rawDate: quoteDate,
                form: {
                    nombre: `${patient.first_name || ''} ${patient.last_name || ''}`.trim().toUpperCase(),
                    cedula: patient.cedula || '',
                    representante: (patient.representative || '').toUpperCase(),
                    cedulaRepresentante: patient.representative_cedula || '',
                    fechaNacimiento: patient.birth_date || '',
                    direccion: patient.address || '',
                    telefono: patient.phone || '',
                    correo: patient.email || '',
                    ocupacion: patient.occupation || '',
                    discountPercentage: savedDiscount
                },
                odontogramaData: r.odontogram_data || {},
                treatments: r.treatments ? r.treatments.map(t => ({
                    ...t,
                    total_final: t.total_final !== undefined ? t.total_final : ((parseFloat(t.precio)||0)*(parseInt(t.cantidad)||1))
                })) : [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]
            });
            setShowConsultaModal(true);
        } else {
            const records = await fetchClinicalRecords(patient.id);
            const quoteR = records?.find(r => r.reason_for_consultation?.includes('Cotización / Presupuesto'));
            if (quoteR) {
                const quoteDate = new Date(quoteR.updated_at || quoteR.created_at);
                const savedDiscount = quoteR.diagnostic_plans?.discountPercentage !== undefined 
                    ? quoteR.diagnostic_plans.discountPercentage 
                    : 0;

                setSelectedQuote({
                    id: quoteR.id,
                    db_patient_id: patient.id,
                    status: 'Cotización',
                    date: quoteDate.toLocaleDateString(),
                    form: {
                        nombre: `${patient.first_name || ''} ${patient.last_name || ''}`.trim().toUpperCase(),
                        cedula: patient.cedula || '',
                        representante: (patient.representative || '').toUpperCase(),
                        cedulaRepresentante: patient.representative_cedula || '',
                        fechaNacimiento: patient.birth_date || '',
                        direccion: patient.address || '',
                        telefono: patient.phone || '',
                        correo: patient.email || '',
                        ocupacion: patient.occupation || '',
                        discountPercentage: savedDiscount
                    },
                    odontogramaData: quoteR.odontogram_data || {},
                    treatments: quoteR.treatments || [{ od: '', procedimiento: '', cantidad: 1, precio: 0, total_final: 0 }]
                });
                setShowConsultaModal(true);
            } else {
                if (window.confirm('Este paciente aún no tiene presupuesto ni receta.\n\n¿Crear uno ahora con sus datos y la fórmula de su último examen?')) {
                    window.location.assign(`/clinica/consultas?patient_id=${patient.id}`);
                }
            }
        }
    };

    const handleEdit = (patient) => {
        handleOpenVisualRecord(patient, null, 'editar');
    };

    const confirmCloseModal = (closeFn) => {
        setConfirmAction(() => closeFn);
    };

    // Cálculos de contadores para ergonomía ISO 9001
    const pacientesEnVista = pacientes.filter(p => filtroVista === 'clinico' ? p.is_clinical !== false : p.is_clinical === false);
    const totalClinicos = pacientes.filter(p => p.is_clinical !== false).length;
    const totalAgenda = pacientes.filter(p => p.is_clinical === false).length;

    const countAlDia = pacientesEnVista.filter(p => getCheckupStatus(p).status === 'ok').length;
    const countProximos = pacientesEnVista.filter(p => getCheckupStatus(p).status === 'upcoming').length;
    const countVencidos = pacientesEnVista.filter(p => getCheckupStatus(p).status === 'expired').length;

    const filteredPacientes = pacientesEnVista.filter(p => {
        const checkup = getCheckupStatus(p);
        if (filtroControl === 'al_dia' && checkup.status !== 'ok') return false;
        if (filtroControl === 'proximo' && checkup.status !== 'upcoming') return false;
        if (filtroControl === 'vencido' && checkup.status !== 'expired') return false;

        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase().trim();
        const fullName = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase();
        const cedula = (p.cedula || '').toLowerCase();
        const phone = (p.phone || '').toLowerCase();
        return fullName.includes(term) || cedula.includes(term) || phone.includes(term);
    });

    const getInitials = (patient) => {
        const f = (patient?.first_name || '').charAt(0).toUpperCase();
        const l = (patient?.last_name || '').charAt(0).toUpperCase();
        return `${f}${l}` || 'VC';
    };

    return (
        <div className="container-fluid p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif" }}>
            
            {/* ── Encabezado Principal ── */}
            <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                <div>
                    <h2 style={{ color: '#111827', fontWeight: '800', margin: 0, letterSpacing: '-0.02em' }}>
                        Pacientes
                    </h2>
                    <p style={{ color: '#6b7280', margin: '4px 0 0 0', fontSize: '0.9rem' }}>
                        Directorio de pacientes, control de salud visual y consultas de optometría
                    </p>
                </div>
                <button 
                    onClick={handleCreate}
                    className="btn shadow-sm d-flex align-items-center gap-2" 
                    style={{ backgroundColor: '#111827', color: '#ffffff', fontWeight: '700', borderRadius: '8px', padding: '10px 18px', border: 'none' }}
                >
                    <i className="bi bi-person-plus-fill"></i>
                    <span>Nuevo Paciente</span>
                </button>
            </div>

            {/* ── Cumpleaños próximos (felicitación automática por correo) ── */}
            <CumpleanosProximos onVerPaciente={(id) => {
                const p = pacientes.find(x => String(x.id) === String(id));
                if (p) setFichaPatient(p);
            }} />

            {/* ── Barra de Herramientas y Filtros (Ergonómica ISO 9001) ── */}
            <div className="card shadow-sm border-0 mb-4" style={{ borderRadius: '12px', backgroundColor: '#ffffff' }}>
                <div className="card-body p-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
                    
                    {/* Filtros Izquierda: Tipo de Vista + Control de Recordatorio */}
                    <div className="d-flex flex-wrap align-items-center gap-2">
                        {/* Selector de Segmento */}
                        <div className="btn-group p-1" style={{ backgroundColor: '#f3f4f6', borderRadius: '10px' }}>
                            <button 
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: filtroVista === 'clinico' ? '#111827' : 'transparent',
                                    color: filtroVista === 'clinico' ? '#ffffff' : '#4b5563',
                                    fontWeight: '700',
                                    borderRadius: '8px',
                                    padding: '6px 14px',
                                    border: 'none',
                                    transition: 'all 0.15s ease'
                                }}
                                onClick={() => { setFiltroVista('clinico'); setFiltroControl('todos'); }}
                            >
                                Pacientes Clínicos ({totalClinicos})
                            </button>
                            <button 
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: filtroVista === 'agenda' ? '#111827' : 'transparent',
                                    color: filtroVista === 'agenda' ? '#ffffff' : '#4b5563',
                                    fontWeight: '700',
                                    borderRadius: '8px',
                                    padding: '6px 14px',
                                    border: 'none',
                                    transition: 'all 0.15s ease'
                                }}
                                onClick={() => { setFiltroVista('agenda'); setFiltroControl('todos'); }}
                            >
                                Solo Agenda ({totalAgenda})
                            </button>
                        </div>

                        {/* Separador vertical */}
                        <div style={{ width: '1px', height: '24px', backgroundColor: '#e5e7eb', margin: '0 4px' }} className="d-none d-md-block" />

                        {/* Filtros de Control Visual */}
                        <div className="d-flex align-items-center gap-1">
                            <button
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: filtroControl === 'todos' ? '#e5e7eb' : 'transparent',
                                    color: '#111827',
                                    fontWeight: filtroControl === 'todos' ? '700' : '500',
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    border: '1px solid',
                                    borderColor: filtroControl === 'todos' ? '#d1d5db' : 'transparent'
                                }}
                                onClick={() => setFiltroControl('todos')}
                            >
                                Todos ({pacientesEnVista.length})
                            </button>
                            <button
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: filtroControl === 'al_dia' ? '#d1fae5' : 'transparent',
                                    color: '#065f46',
                                    fontWeight: filtroControl === 'al_dia' ? '700' : '500',
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    border: '1px solid',
                                    borderColor: filtroControl === 'al_dia' ? '#6ee7b7' : '#e5e7eb'
                                }}
                                onClick={() => setFiltroControl('al_dia')}
                            >
                                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#10b981', marginRight: '6px' }} />
                                Al día ({countAlDia})
                            </button>
                            <button
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: filtroControl === 'proximo' ? '#fef3c7' : 'transparent',
                                    color: '#92400e',
                                    fontWeight: filtroControl === 'proximo' ? '700' : '500',
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    border: '1px solid',
                                    borderColor: filtroControl === 'proximo' ? '#fcd34d' : '#e5e7eb'
                                }}
                                onClick={() => setFiltroControl('proximo')}
                            >
                                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#f59e0b', marginRight: '6px' }} />
                                Próximos ({countProximos})
                            </button>
                            <button
                                className="btn btn-sm"
                                style={{
                                    backgroundColor: filtroControl === 'vencido' ? '#fee2e2' : 'transparent',
                                    color: '#991b1b',
                                    fontWeight: filtroControl === 'vencido' ? '700' : '500',
                                    borderRadius: '6px',
                                    padding: '4px 10px',
                                    fontSize: '0.8rem',
                                    border: '1px solid',
                                    borderColor: filtroControl === 'vencido' ? '#f87171' : '#e5e7eb'
                                }}
                                onClick={() => setFiltroControl('vencido')}
                            >
                                <span style={{ display: 'inline-block', width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#ef4444', marginRight: '6px' }} />
                                Vencidos ({countVencidos})
                            </button>
                        </div>
                    </div>

                    {/* Buscador Derecha */}
                    <div style={{ position: 'relative', width: '100%', maxWidth: '320px' }}>
                        <i 
                            className="bi bi-search" 
                            style={{ 
                                position: 'absolute', 
                                left: '12px', 
                                top: '50%', 
                                transform: 'translateY(-50%)', 
                                color: '#9ca3af',
                                pointerEvents: 'none'
                            }} 
                        />
                        <input 
                            type="text" 
                            className="form-control" 
                            placeholder="Buscar paciente, cédula o teléfono..." 
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ 
                                borderRadius: '8px', 
                                paddingLeft: '36px', 
                                paddingRight: searchTerm ? '32px' : '12px',
                                fontSize: '0.875rem',
                                border: '1px solid #d1d5db'
                            }}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                style={{
                                    position: 'absolute',
                                    right: '10px',
                                    top: '50%',
                                    transform: 'translateY(-50%)',
                                    background: 'transparent',
                                    border: 'none',
                                    color: '#9ca3af',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    lineHeight: 1
                                }}
                            >
                                &times;
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Tabla de Pacientes (Limpia y Ergonomía Certificada) ── */}
            <div className="card shadow-sm border-0" style={{ borderRadius: '12px', overflow: 'visible', backgroundColor: '#ffffff' }}>
                <div className="table-responsive" style={{ overflow: 'visible' }}>
                    <table className="table align-middle mb-0" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
                        <thead style={{ backgroundColor: '#111827', color: '#ffffff' }}>
                            <tr>
                                <th className="py-3 px-4 border-0" style={{ fontWeight: '700', fontSize: '0.85rem', letterSpacing: '0.04em' }}>PACIENTE</th>
                                <th className="py-3 px-4 border-0" style={{ fontWeight: '700', fontSize: '0.85rem', letterSpacing: '0.04em' }}>CONTACTO</th>
                                <th className="py-3 px-4 border-0" style={{ fontWeight: '700', fontSize: '0.85rem', letterSpacing: '0.04em' }}>CONTROL VISUAL / RECORDATORIO</th>
                                <th className="py-3 px-4 border-0" style={{ fontWeight: '700', fontSize: '0.85rem', letterSpacing: '0.04em' }}>EDAD</th>
                                <th className="py-3 px-4 border-0 text-end" style={{ fontWeight: '700', fontSize: '0.85rem', letterSpacing: '0.04em' }}>ACCIONES</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5">
                                        <div className="spinner-border text-dark spinner-border-sm me-2" role="status" />
                                        <span style={{ color: '#6b7280', fontWeight: '500' }}>Cargando registros de pacientes...</span>
                                    </td>
                                </tr>
                            ) : filteredPacientes.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-5" style={{ color: '#6b7280' }}>
                                        <i className="bi bi-people" style={{ fontSize: '2.5rem', opacity: 0.3, display: 'block', marginBottom: '8px' }}></i>
                                        <strong>No se encontraron pacientes</strong>
                                        <div style={{ fontSize: '0.85rem', marginTop: '4px' }}>
                                            {searchTerm ? 'Intente con otro término de búsqueda.' : 'No hay pacientes en esta categoría.'}
                                        </div>
                                    </td>
                                </tr>
                            ) : (
                                filteredPacientes.map((p) => {
                                    const checkup = getCheckupStatus(p);
                                    const waLink = getWhatsAppRecallLink(p, checkup);
                                    const isDropdownOpen = activeDropdownId === p.id;

                                    // Chequear si tiene observaciones pendientes
                                    let obs = p.clinical_observations;
                                    if (typeof obs === 'string') {
                                        try { obs = JSON.parse(obs); } catch(e) { obs = []; }
                                    }
                                    const hasPendingObs = Array.isArray(obs) && obs.some(o => !o.resolved);

                                    return (
                                        <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                            
                                            {/* Columna 1: Paciente (Avatar + Nombre + Cédula) */}
                                            <td className="py-3 px-4">
                                                <div className="d-flex align-items-center gap-3">
                                                    <div 
                                                        style={{ 
                                                            width: '38px', 
                                                            height: '38px', 
                                                            borderRadius: '50%', 
                                                            backgroundColor: '#111827', 
                                                            color: '#ffffff', 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center', 
                                                            fontWeight: '700', 
                                                            fontSize: '0.85rem',
                                                            flexShrink: 0
                                                        }}
                                                    >
                                                        {getInitials(p)}
                                                    </div>
                                                    <div>
                                                        <div style={{ fontWeight: '700', color: '#111827', fontSize: '0.95rem', lineHeight: 1.2 }}>
                                                            {p.first_name || 'Paciente'} {p.last_name || ''}
                                                        </div>
                                                        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '3px' }}>
                                                            C.I.: <strong>{p.cedula || 'Sin documento'}</strong>
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>

                                            {/* Columna 2: Contacto */}
                                            <td className="py-3 px-4">
                                                <div style={{ fontSize: '0.88rem', color: '#111827', fontWeight: '500' }}>
                                                    {p.phone || <span style={{ color: '#9ca3af' }}>Sin teléfono</span>}
                                                </div>
                                                {p.address && (
                                                    <div style={{ fontSize: '0.78rem', color: '#6b7280', maxWidth: '220px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                        {p.address}
                                                    </div>
                                                )}
                                            </td>

                                            {/* Columna 3: Control Visual / Recordatorio */}
                                            <td className="py-3 px-4">
                                                <div className="d-flex align-items-center gap-2 flex-wrap">
                                                    <span 
                                                        className="badge px-2 py-1" 
                                                        style={{ ...checkup.badgeStyle, fontSize: '0.78rem', fontWeight: '600' }}
                                                        title={p.last_checkup_date ? `Último examen: ${p.last_checkup_date}` : 'Sin fecha previa'}
                                                    >
                                                        {checkup.label}
                                                    </span>
                                                    {waLink && (checkup.status === 'expired' || checkup.status === 'upcoming') && (
                                                        <a 
                                                            href={waLink} 
                                                            target="_blank" 
                                                            rel="noopener noreferrer"
                                                            className="btn btn-sm d-inline-flex align-items-center gap-1 text-decoration-none shadow-sm"
                                                            style={{ backgroundColor: '#25D366', color: '#ffffff', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700' }}
                                                            title="Enviar recordatorio de control periódico por WhatsApp"
                                                        >
                                                            <i className="bi bi-whatsapp"></i> Recordar
                                                        </a>
                                                    )}
                                                    {p.email && p.next_checkup_date && (
                                                        <button
                                                            type="button"
                                                            onClick={() => setRecordatorioPatient(p)}
                                                            className="btn btn-sm d-inline-flex align-items-center gap-1 shadow-sm"
                                                            style={{ backgroundColor: '#111827', color: '#ffffff', padding: '2px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: '700', border: 'none' }}
                                                            title={`Enviar recordatorio de control por correo a ${p.email}`}
                                                        >
                                                            <i className="bi bi-envelope-fill" style={{ color: '#ffffff' }}></i>
                                                            <span style={{ color: '#ffffff' }}>Correo</span>
                                                        </button>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Columna 4: Edad */}
                                            <td className="py-3 px-4">
                                                <span 
                                                    className="badge" 
                                                    style={{ backgroundColor: '#f3f4f6', color: '#374151', border: '1px solid #e5e7eb', fontSize: '0.8rem', fontWeight: '600', whiteSpace: 'nowrap' }}
                                                >
                                                    {(p.age !== undefined && p.age !== null) ? `${p.age} años` : 'N/A'}
                                                </span>
                                            </td>

                                            {/* Columna 5: Acciones (Diseño Ergonómico de 2 Botones + Menú) */}
                                            <td className="py-3 px-4 text-end">
                                                <div className="d-inline-flex align-items-center gap-1">
                                                    
                                                    {/* Botón Principal: Historia Clínica Visual */}
                                                    <button 
                                                        onClick={() => handleOpenVisualRecord(p)} 
                                                        className="btn btn-sm text-white d-inline-flex align-items-center gap-1 shadow-sm" 
                                                        style={{ backgroundColor: '#111827', color: '#ffffff', border: '1px solid #111827', borderRadius: '6px', fontWeight: '600', padding: '5px 10px', fontSize: '0.8rem' }} 
                                                        title="Ver la última historia clínica visual guardada"
                                                    >
                                                        <i className="bi bi-eye-fill" style={{ color: '#ffffff' }}></i>
                                                        <span style={{ color: '#ffffff' }}>Historia actual</span>
                                                    </button>

                                                    {/* Examen nuevo: la historia anterior queda intacta */}
                                                    <button 
                                                        onClick={() => handleOpenVisualRecord(p, null, 'nuevo')} 
                                                        className="btn btn-sm d-inline-flex align-items-center gap-1" 
                                                        style={{ backgroundColor: '#b59a4a', border: '1px solid #b59a4a', color: '#ffffff', borderRadius: '6px', fontWeight: '600', padding: '5px 10px', fontSize: '0.8rem' }} 
                                                        title="Registrar un examen nuevo (la historia anterior no se modifica)"
                                                    >
                                                        <i className="bi bi-plus-lg" style={{ color: '#ffffff' }}></i>
                                                        <span style={{ color: '#ffffff' }}>Nueva historia</span>
                                                    </button>

                                                    {/* Botón Secundario: Fórmulas / Prescripciones Dióptricas */}
                                                    <button 
                                                        onClick={() => handleOpenFormulasEvolution(p)} 
                                                        className="btn btn-sm d-inline-flex align-items-center gap-1" 
                                                        style={{ backgroundColor: '#ffffff', border: '1.5px solid #111827', color: '#111827', borderRadius: '6px', fontWeight: '600', padding: '5px 8px', fontSize: '0.8rem' }} 
                                                        title="Evolución y Fórmulas Anteriores"
                                                    >
                                                        <i className="bi bi-clock-history"></i>
                                                        <span>Fórmulas</span>
                                                    </button>

                                                    {/* Botón Rápido: recordatorio de control por correo */}
                                                    <button
                                                        onClick={() => setRecordatorioPatient(p)}
                                                        className="btn btn-sm d-inline-flex align-items-center gap-1"
                                                        style={{ backgroundColor: '#ffffff', border: '1.5px solid #b59a4a', color: '#8a7330', borderRadius: '6px', fontWeight: '600', padding: '5px 8px', fontSize: '0.8rem' }}
                                                        title="Enviar recordatorio de control por correo"
                                                    >
                                                        <i className="bi bi-envelope"></i>
                                                        <span>Recordar</span>
                                                    </button>

                                                    {/* Botón Rápido: Editar Datos */}
                                                    <button 
                                                        onClick={() => handleEdit(p)} 
                                                        className="btn btn-sm btn-light border" 
                                                        style={{ borderRadius: '6px', padding: '5px 8px', color: '#374151' }} 
                                                        title="Editar / corregir la última historia clínica"
                                                    >
                                                        <i className="bi bi-pencil"></i>
                                                    </button>

                                                    {/* Menú Desplegable 'Más Opciones' */}
                                                    <div className="position-relative d-inline-block patient-dropdown-container">
                                                        <button 
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                setActiveDropdownId(isDropdownOpen ? null : p.id);
                                                            }} 
                                                            className="btn btn-sm btn-light border position-relative" 
                                                            style={{ borderRadius: '6px', padding: '5px 8px', color: '#374151' }} 
                                                            title="Más opciones de gestión"
                                                        >
                                                            <i className="bi bi-three-dots-vertical"></i>
                                                            {hasPendingObs && (
                                                                <span 
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: '-3px',
                                                                        right: '-3px',
                                                                        width: '8px',
                                                                        height: '8px',
                                                                        borderRadius: '50%',
                                                                        backgroundColor: '#ef4444',
                                                                        border: '1.5px solid #ffffff'
                                                                    }}
                                                                />
                                                            )}
                                                        </button>

                                                        {isDropdownOpen && (
                                                            <div 
                                                                style={{
                                                                    position: 'absolute',
                                                                    right: 0,
                                                                    top: 'calc(100% + 4px)',
                                                                    zIndex: 1050,
                                                                    minWidth: '240px',
                                                                    width: 'max-content',
                                                                    backgroundColor: '#ffffff',
                                                                    borderRadius: '10px',
                                                                    boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                                                                    border: '1px solid #e5e7eb',
                                                                    padding: '6px',
                                                                    textAlign: 'left'
                                                                }}
                                                            >
                                                                <button 
                                                                    type="button" 
                                                                    className="dropdown-item py-2 px-3 d-flex align-items-center gap-2" 
                                                                    style={{ borderRadius: '6px', fontSize: '0.85rem', fontWeight: '500', color: '#111827', border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                                                                    onClick={() => handleView(p)}
                                                                >
                                                                    <i className="bi bi-person-vcard text-muted"></i>
                                                                    <span>Ver Ficha General</span>
                                                                </button>

                                                                <button 
                                                                    type="button" 
                                                                    className="dropdown-item py-2 px-3 d-flex align-items-center gap-2" 
                                                                    style={{ borderRadius: '6px', fontSize: '0.85rem', fontWeight: '500', color: '#111827', border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                                                                    onClick={() => handleViewConsulta(p)}
                                                                >
                                                                    <i className="bi bi-receipt text-muted"></i>
                                                                    <span>Presupuesto & Receta</span>
                                                                </button>

                                                                <button 
                                                                    type="button" 
                                                                    className="dropdown-item py-2 px-3 d-flex align-items-center gap-2" 
                                                                    style={{ borderRadius: '6px', fontSize: '0.85rem', fontWeight: '500', color: '#111827', border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                                                                    onClick={() => handleRegistroFotografico(p)}
                                                                >
                                                                    <i className="bi bi-camera text-muted"></i>
                                                                    <span>Registro Fotográfico</span>
                                                                </button>

                                                                <button
                                                                    type="button"
                                                                    className="dropdown-item py-2 px-3 d-flex align-items-center gap-2"
                                                                    style={{ borderRadius: '6px', fontSize: '0.85rem', fontWeight: '500', color: '#111827', border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                                                                    onClick={() => { setActiveDropdownId(null); setFacturaPatient(p); }}
                                                                >
                                                                    <i className="bi bi-receipt-cutoff text-muted"></i>
                                                                    <span>Facturar</span>
                                                                </button>

                                                                <button 
                                                                    type="button" 
                                                                    className="dropdown-item py-2 px-3 d-flex align-items-center gap-2" 
                                                                    style={{ borderRadius: '6px', fontSize: '0.85rem', fontWeight: '500', color: '#111827', border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                                                                    onClick={() => { setActiveDropdownId(null); navigate(`/clinica/certificado-visual?patient_id=${p.id}`); }}
                                                                >
                                                                    <i className="bi bi-file-earmark-medical text-muted"></i>
                                                                    <span>Certificado Visual</span>
                                                                </button>

                                                                <button 
                                                                    type="button" 
                                                                    className="dropdown-item py-2 px-3 d-flex align-items-center justify-content-between" 
                                                                    style={{ borderRadius: '6px', fontSize: '0.85rem', fontWeight: '500', color: '#111827', border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                                                                    onClick={() => handleOpenObservations(p)}
                                                                >
                                                                    <div className="d-flex align-items-center gap-2">
                                                                        <i className="bi bi-lightbulb text-muted"></i>
                                                                        <span>Observaciones Clínicas</span>
                                                                    </div>
                                                                    {hasPendingObs && (
                                                                        <span className="badge bg-danger rounded-pill" style={{ fontSize: '0.7rem' }}>Pendiente</span>
                                                                    )}
                                                                </button>

                                                                {filtroVista === 'agenda' && (
                                                                    <button 
                                                                        type="button" 
                                                                        className="dropdown-item py-2 px-3 d-flex align-items-center gap-2 text-success" 
                                                                        style={{ borderRadius: '6px', fontSize: '0.85rem', fontWeight: '600', border: 'none', background: 'transparent', width: '100%', textAlign: 'left' }}
                                                                        onClick={() => handlePromoteToClinical(p)}
                                                                    >
                                                                        <i className="bi bi-person-check-fill"></i>
                                                                        <span>Mover a Historial Clínico</span>
                                                                    </button>
                                                                )}

</div>
                                                        )}
                                                    </div>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* ── Modales del Sistema ── */}
            {showCertificadoModal && (
                <CertificadoMedico 
                    patient={selectedPatient} 
                    clinicalData={clinicalData}
                    onSaveSuccess={() => {
                        loadPacientes();
                        if (selectedPatient?.id) loadPatientClinicalData(selectedPatient.id);
                    }}
                    onCancel={(direct = false) => {
                        if (direct === true) {
                            setShowCertificadoModal(false);
                        } else {
                            confirmCloseModal(() => setShowCertificadoModal(false));
                        }
                    }} 
                />
            )}

            {showConsultaModal && (
                <VisorConsultaModal 
                    patient={selectedPatient} 
                    quote={selectedQuote} 
                    onCancel={() => confirmCloseModal(() => setShowConsultaModal(false))} 
                />
            )}

            {showRegistroFotograficoModal && (
                <RegistroFotografico 
                    patient={selectedPatient} 
                    onCancel={() => confirmCloseModal(() => setShowRegistroFotograficoModal(false))} 
                />
            )}

            {showObsModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(17, 24, 39, 0.75)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ width: '90%', maxWidth: '600px', maxHeight: '80vh', backgroundColor: '#fff', borderRadius: '12px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
                        <div style={{ padding: '15px 20px', backgroundColor: '#111827', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0, fontWeight: 'bold' }}>
                                Observaciones Clínicas - {selectedObsPatient?.first_name} {selectedObsPatient?.last_name}
                            </h5>
                            <button type="button" onClick={() => confirmCloseModal(() => setShowObsModal(false))} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', lineHeight: 1, padding: 0, cursor: 'pointer' }}>&times;</button>
                        </div>
                        
                        <div style={{ padding: '20px', overflowY: 'auto', flex: 1, backgroundColor: '#f8f9fa' }}>
                            {observations.length === 0 ? (
                                <p className="text-muted text-center my-4">No hay observaciones registradas para este paciente.</p>
                            ) : (
                                observations.map((obs, index) => (
                                    <div key={obs.id || index} style={{ backgroundColor: '#fff', border: '1px solid #ddd', borderRadius: '8px', padding: '15px', marginBottom: '15px', position: 'relative' }}>

                                        <div className="mb-2 pe-4">
                                            <textarea 
                                                className="form-control" 
                                                rows="2" 
                                                value={obs.text} 
                                                onChange={(e) => handleUpdateObservation(index, 'text', e.target.value)}
                                                placeholder="Escribe la observación clínica aquí..."
                                                style={{ resize: 'vertical' }}
                                            ></textarea>
                                        </div>
                                        
                                        <div className="form-check form-switch mt-2">
                                            <input 
                                                className="form-check-input" 
                                                type="checkbox" 
                                                id={`obsResolvedSwitch_${index}`} 
                                                checked={obs.resolved} 
                                                onChange={(e) => handleUpdateObservation(index, 'resolved', e.target.checked)}
                                                style={{ cursor: 'pointer', height: '20px', width: '40px' }}
                                            />
                                            <label className="form-check-label fw-bold ms-2" htmlFor={`obsResolvedSwitch_${index}`} style={{ cursor: 'pointer', color: obs.resolved ? '#10b981' : '#dc3545', paddingTop: '2px' }}>
                                                {obs.resolved ? (
                                                    <><i className="bi bi-check-circle-fill me-1"></i> Resuelta</>
                                                ) : (
                                                    <><i className="bi bi-exclamation-circle-fill me-1"></i> Pendiente (No resuelta)</>
                                                )}
                                            </label>
                                        </div>
                                    </div>
                                ))
                            )}
                            
                            <div className="text-center mt-3">
                                <button type="button" className="btn btn-light border" onClick={handleAddObservation}>
                                    <i className="bi bi-plus-circle me-1"></i> Agregar Observación
                                </button>
                            </div>
                        </div>

                        <div style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'flex-end', backgroundColor: '#fff' }}>
                            <button type="button" className="btn btn-light border me-2" onClick={() => confirmCloseModal(() => setShowObsModal(false))}>Cancelar</button>
                            <button type="button" className="btn btn-dark" onClick={handleSaveObservations} disabled={savingObs}>
                                {savingObs ? 'Guardando...' : 'Guardar Observaciones'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showMissingFieldsModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(17, 24, 39, 0.75)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <div style={{ width: '90%', maxWidth: '420px', backgroundColor: '#fff', borderRadius: '12px', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.3)', overflow: 'hidden' }}>
                        <div style={{ padding: '15px 20px', backgroundColor: '#111827', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h5 style={{ margin: 0, fontWeight: 'bold' }}>
                                Datos Incompletos
                            </h5>
                            <button type="button" onClick={() => setShowMissingFieldsModal(false)} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.5rem', lineHeight: 1, padding: 0, cursor: 'pointer' }}>&times;</button>
                        </div>
                        <div style={{ padding: '20px', backgroundColor: '#f8f9fa', textAlign: 'center' }}>
                            <p style={{ fontSize: '0.95rem', marginBottom: '15px', color: '#374151' }}>
                                No tiene los campos completos como para emitir un certificado médico. Por favor complete los siguientes campos:
                            </p>
                            <ul style={{ listStyleType: 'none', padding: 0, margin: 0, color: '#dc3545', fontWeight: 'bold', fontSize: '0.9rem' }}>
                                {missingFields.map((field, idx) => (
                                    <li key={idx} style={{ marginBottom: '6px' }}>
                                        <i className="bi bi-exclamation-circle me-2"></i>{field}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div style={{ padding: '15px 20px', borderTop: '1px solid #dee2e6', display: 'flex', justifyContent: 'center', backgroundColor: '#fff' }}>
                            <button type="button" className="btn btn-dark" onClick={() => setShowMissingFieldsModal(false)}>
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}
            
            {/* Modal de Historia Clínica Visual (Ergonómico, centrado con backdrop blur) */}
            {showVisualModal && (
                <div 
                    style={{ 
                        position: 'fixed', 
                        top: 0, 
                        left: 0, 
                        width: '100vw', 
                        height: '100vh', 
                        backgroundColor: 'rgba(17, 24, 39, 0.75)', 
                        backdropFilter: 'blur(4px)', 
                        zIndex: 9999, 
                        display: 'flex', 
                        justifyContent: 'center', 
                        alignItems: 'center', 
                        padding: '16px' 
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) setShowVisualModal(false);
                    }}
                >
                    <HistoriaClinicaVisual 
                        key={visualKey}
                        isViewOnly={visualViewOnly}
                        onNuevoExamen={selectedPatientForVisual ? () => handleOpenVisualRecord(selectedPatientForVisual, null, 'nuevo') : undefined}
                        patient={selectedPatientForVisual}
                        initialRecord={selectedVisualRecord}
                        previousRecord={previousVisualRecord}
                        onSaved={(_, opciones) => {
                            if (opciones?.cerrar !== false) setShowVisualModal(false);
                            loadPacientes();
                        }}
                        onCancel={() => setShowVisualModal(false)}
                    />
                </div>
            )}

            {facturaPatient && (
                <ModalFactura
                    paciente={facturaPatient}
                    origen="paciente"
                    origenId=""
                    onClose={() => setFacturaPatient(null)}
                />
            )}

            {recordatorioPatient && (
                <ModalRecordatorio
                    patient={recordatorioPatient}
                    onClose={() => setRecordatorioPatient(null)}
                    onEnviado={() => loadPacientes()}
                />
            )}

            {/* Ficha general del paciente */}
            {fichaPatient && (
                <FichaPacienteModal
                    patient={fichaPatient}
                    onClose={() => setFichaPatient(null)}
                    onVerHistoria={(h) => { setFichaPatient(null); handleOpenVisualRecord(fichaPatient, h, 'ver'); }}
                    onEditarHistoria={(h) => { setFichaPatient(null); handleOpenVisualRecord(fichaPatient, h, 'editar'); }}
                    onNuevoExamen={() => { setFichaPatient(null); handleOpenVisualRecord(fichaPatient, null, 'nuevo'); }}
                    onPresupuesto={() => { setFichaPatient(null); handleViewConsulta(fichaPatient); }}
                />
            )}

            {/* Modal de Evolución de Fórmulas */}
            {showFormulasModal && (
                <HistorialFormulasModal 
                    patient={selectedPatientForVisual}
                    records={visualRecordsList}
                    onSelectRecord={(rec) => {
                        setShowFormulasModal(false);
                        handleOpenVisualRecord(selectedPatientForVisual, rec, 'ver');
                    }}
                    onClose={() => setShowFormulasModal(false)}
                />
            )}
            
            <ConfirmModal 
                isOpen={!!confirmAction} 
                onClose={() => setConfirmAction(null)} 
                onConfirm={() => {
                    if (confirmAction) confirmAction();
                    setConfirmAction(null);
                }} 
            />
        </div>
    );
};

export default Pacientes;
